'use client';

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MoreVertical,
  FileInput,
  UserPlus,
  PenTool,
  Sparkles,
  CheckCircle,
  ClipboardList,
  Link2,
  Tag,
  Loader2,
  ExternalLink,
  Download,
  X,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import {
  messagingApi,
  Attachment,
  AttachmentAction,
  AttachmentActionExecution,
  DocumentCategory,
} from '@/services/messagingApi';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { CloudFileCard } from './cloud/CloudFileCard';
import { kindFromMime, parseCloudLink } from './cloud/cloudLinks';
import { toSafeHref } from './linkify';

function readBlobText(blob: Blob): Promise<string> {
  if (typeof (blob as any)?.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

interface AttachmentActionsProps {
  attachment: Attachment;
  channelId: number;
  memberId?: number;
  enrollmentId?: number;
  onActionComplete?: (result: AttachmentActionExecution) => void;
}

// Map action icons to Lucide components
const ACTION_ICONS: Record<string, React.ReactNode> = {
  'file-input': <FileInput className="h-4 w-4" />,
  'user-plus': <UserPlus className="h-4 w-4" />,
  'pen-tool': <PenTool className="h-4 w-4" />,
  'sparkles': <Sparkles className="h-4 w-4" />,
  'check-circle': <CheckCircle className="h-4 w-4" />,
  'clipboard-list': <ClipboardList className="h-4 w-4" />,
  'link': <Link2 className="h-4 w-4" />,
  'file': <FileInput className="h-4 w-4" />,
};

// Document categories for classification
const DOCUMENT_CATEGORIES: { value: DocumentCategory; label: string }[] = [
  { value: 'consent', label: 'Consent Form' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'medical_record', label: 'Medical Record' },
  { value: 'id_document', label: 'ID Document' },
  { value: 'proof_of_income', label: 'Proof of Income' },
  { value: 'proof_of_residence', label: 'Proof of Residence' },
  { value: 'employment_verification', label: 'Employment Verification' },
  { value: 'intake', label: 'Intake Form' },
  { value: 'agreement', label: 'Agreement' },
  { value: 'contract', label: 'Contract' },
  { value: 'other', label: 'Other' },
];

export function AttachmentActions({
  attachment,
  channelId,
  memberId,
  enrollmentId,
  onActionComplete,
}: AttachmentActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [executingAction, setExecutingAction] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch available actions for this attachment
  const { data: availableActions = [], isLoading: loadingActions } = useQuery({
    queryKey: ['attachment-actions', attachment.id],
    queryFn: () => messagingApi.getAttachmentActions(attachment.id),
    enabled: isOpen, // Only fetch when dropdown is opened
    staleTime: 30000, // Cache for 30 seconds
  });

  // Execute action mutation
  const executeMutation = useMutation({
    mutationFn: async ({ actionCode, config }: { actionCode: string; config?: any }) => {
      setExecutingAction(actionCode);
      return messagingApi.executeAttachmentAction(attachment.id, actionCode, config);
    },
    onSuccess: (result) => {
      setExecutingAction(null);
      onActionComplete?.(result);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['attachment-links', attachment.id] });
      // Show success notification or handle result
      if (result.status === 'completed') {
        console.log(`Action completed: ${result.result_entity_type} #${result.result_entity_id}`);
      }
    },
    onError: (error) => {
      setExecutingAction(null);
      console.error('Action failed:', error);
    },
  });

  // Classify document mutation
  const classifyMutation = useMutation({
    mutationFn: (category: DocumentCategory) =>
      messagingApi.classifyAttachment(attachment.id, category),
    onSuccess: () => {
      // Invalidate actions query to refresh available actions
      queryClient.invalidateQueries({ queryKey: ['attachment-actions', attachment.id] });
    },
  });

  const handleExecuteAction = (action: AttachmentAction) => {
    // Build context based on available data
    const context: Record<string, any> = {};
    if (memberId) context.member_id = memberId;
    if (enrollmentId) context.enrollment_id = enrollmentId;

    executeMutation.mutate({
      actionCode: action.action_code,
      config: Object.keys(context).length > 0 ? { context } : undefined,
    });
  };

  const getActionIcon = (iconName: string) => {
    return ACTION_ICONS[iconName] || <FileInput className="h-4 w-4" />;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Attachment Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Loading state */}
        {loadingActions && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Available actions */}
        {!loadingActions && availableActions.length > 0 && (
          <>
            {availableActions.map((action) => (
              <DropdownMenuItem
                key={action.action_code}
                onClick={() => handleExecuteAction(action)}
                disabled={executingAction !== null}
                className="cursor-pointer"
              >
                {executingAction === action.action_code ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <span className="mr-2">{getActionIcon(action.action_icon)}</span>
                )}
                <span className="flex-1">{action.action_label}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        {/* No actions available message */}
        {!loadingActions && availableActions.length === 0 && (
          <div className="px-2 py-4 text-center text-sm text-muted-foreground">
            No actions available.
            <br />
            <span className="text-xs">Classify the document first.</span>
          </div>
        )}

        {/* Classify document submenu */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Tag className="mr-2 h-4 w-4" />
            <span>Classify Document</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-48">
            {DOCUMENT_CATEGORIES.map((cat) => (
              <DropdownMenuItem
                key={cat.value}
                onClick={() => classifyMutation.mutate(cat.value)}
                disabled={classifyMutation.isPending}
                className="cursor-pointer"
              >
                {cat.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* View/Download link */}
        {attachment.url && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open in New Tab
              </a>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Inline attachment display with action menu
 */
interface AttachmentDisplayProps {
  attachment: Attachment;
  channelId: number;
  memberId?: number;
  enrollmentId?: number;
  compact?: boolean;
  /** The viewer sent this message — cloud links then show the access note. */
  isOwn?: boolean;
}

/** A Google Drive / OneDrive / SharePoint link attachment (no bytes on our side). */
export function isCloudLinkAttachment(attachment: Attachment): boolean {
  return attachment.source === 'cloud_link';
}

export function AttachmentDisplay({
  attachment,
  channelId,
  memberId,
  enrollmentId,
  compact = false,
  isOwn = false,
}: AttachmentDisplayProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const isImage = attachment.mime_type?.startsWith('image/');
  const isPdf = attachment.mime_type === 'application/pdf';

  // Close the lightbox on Escape.
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen]);

  // Download via a blob so it works cross-origin AND inside the desktop (Tauri)
  // webview — a bare cross-origin <a download> is ignored / opens a chrome-less
  // window the user can't escape. Streams from the backend (CORS-enabled).
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await apiClient.get(
        `/api/messaging/attachments/${attachment.id}/download`,
        { responseType: 'blob' },
      );
      // Defensive: a cloud-link row answers with JSON {source:'cloud_link', url}
      // instead of bytes — open the provider rather than saving the JSON.
      const contentType = String(res.headers?.['content-type'] || (res.data as Blob)?.type || '');
      if (
        res.headers?.['x-attachment-source'] === 'cloud_link' ||
        contentType.includes('application/json')
      ) {
        try {
          const info = JSON.parse(await readBlobText(res.data as Blob));
          if (info?.source === 'cloud_link') {
            const href = toSafeHref(info.url);
            if (href) window.open(href, '_blank', 'noopener,noreferrer');
            return;
          }
        } catch {
          /* not JSON after all — fall through and save it */
        }
      }
      const blobUrl = window.URL.createObjectURL(
        new Blob([res.data], { type: attachment.mime_type || 'application/octet-stream' }),
      );
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Attachment download failed', e);
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Cloud links open at the provider. Never call the S3 download path for them.
  if (isCloudLinkAttachment(attachment)) {
    const provider = attachment.provider || parseCloudLink(attachment.url)?.provider || 'google_drive';
    return (
      <CloudFileCard
        provider={provider}
        kind={kindFromMime(attachment.mime_type, attachment.filename)}
        name={attachment.filename}
        href={attachment.url}
        sizeBytes={attachment.file_size}
        showAccessNote={isOwn}
        attachmentId={attachment.id}
        compact={compact}
        className="mt-0"
        testId="cloud-attachment-card"
      />
    );
  }

  if (compact) {
    return (
      <div className="group inline-flex items-center gap-2 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-sm">
        <FileInput className="h-4 w-4 text-muted-foreground" />
        <a
          href={attachment.url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline truncate max-w-[200px]"
        >
          {attachment.filename}
        </a>
        <span className="text-xs text-muted-foreground">
          ({formatFileSize(attachment.file_size)})
        </span>
        <AttachmentActions
          attachment={attachment}
          channelId={channelId}
          memberId={memberId}
          enrollmentId={enrollmentId}
        />
      </div>
    );
  }

  return (
    <div className="group relative border rounded-lg p-3 bg-white dark:bg-slate-900 max-w-sm">
      {/* Image preview — click to view full resolution in an in-app lightbox */}
      {isImage && attachment.thumbnail_url && (
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          title="View full resolution"
          className="mb-2 block w-full text-left"
        >
          <img
            src={attachment.thumbnail_url}
            alt={attachment.filename}
            className="rounded max-h-48 w-auto cursor-zoom-in hover:opacity-90 transition-opacity"
          />
        </button>
      )}

      {/* PDF icon */}
      {isPdf && (
        <div className="mb-2 flex items-center justify-center h-24 bg-red-50 dark:bg-red-900/20 rounded">
          <FileInput className="h-12 w-12 text-red-500" />
        </div>
      )}

      {/* File info */}
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <a
            href={attachment.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-sm text-primary hover:underline block truncate"
          >
            {attachment.filename}
          </a>
          <div className="text-xs text-muted-foreground">
            {attachment.file_type?.toUpperCase()} • {formatFileSize(attachment.file_size)}
          </div>
          <div className="flex items-center gap-3 mt-1">
            {isImage && (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="text-xs text-primary hover:underline"
              >
                View full size
              </button>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
            >
              {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
              Download
            </button>
          </div>
        </div>
        <AttachmentActions
          attachment={attachment}
          channelId={channelId}
          memberId={memberId}
          enrollmentId={enrollmentId}
        />
      </div>

      {/* In-app full-resolution lightbox — close via X, Esc, or click outside.
          Avoids opening a chrome-less new tab (which traps users in the desktop app). */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <div
            className="absolute top-3 right-3 flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/90 text-gray-800 text-sm font-medium hover:bg-white disabled:opacity-50"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download
            </button>
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              aria-label="Close"
              className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-white/90 text-gray-800 hover:bg-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <img
            src={attachment.url || attachment.thumbnail_url || ''}
            alt={attachment.filename}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[92vw] object-contain rounded shadow-2xl"
          />
          <div
            className="absolute bottom-3 left-0 right-0 text-center text-white/80 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            {attachment.filename} • press Esc or click outside to close
          </div>
        </div>
      )}
    </div>
  );
}

export default AttachmentActions;
